/**
 * `ReportAuthoringChat` — the /reports/new surface.
 *
 * Permanent side-by-side split: streaming agent chat on the left
 * (fixed width, white), live report preview on the right — the panel
 * IS the product; the chat exists to drive it.
 *
 * The transcript renders with the SAME shared message components as
 * Ask Flint (`components/ai-messages/`): streamed markdown text via
 * `AIMessage`, tool rows for `propose_report` / `save_report`, the
 * ThinkingMessage while waiting. The composer is also the same
 * `ChatComposer` used by /ask, so keyboard/paste/dictation behaviour
 * stays consistent across the two AI surfaces.
 */
import { ArrowLeft, Loader2 } from "lucide-react";
import { Link } from "@tanstack/react-router";
import type { UIMessage } from "ai";
import { Conversation, ConversationContent, ConversationScrollButton } from "@flint/ui";
import { AIMessage } from "../../components/ai-messages/ai-message.tsx";
import { HumanMessage } from "../../components/ai-messages/human-message.tsx";
import { ThinkingMessage } from "../../components/ai-messages/thinking-message.tsx";
import { ChatComposer } from "../ai-chat/chat-composer.tsx";
import { PreviewPane, type PendingDraft } from "./preview-pane.tsx";
import { SuggestedPrompts } from "./suggested-prompts.tsx";
import { useReportAuthoringChat } from "./use-report-authoring-chat.ts";
// Streamdown palette overrides scoped under `.flint-ai-chat-surface`
// (set on the chat pane below) — same fixes Ask Flint needs.
import "../../components/ai-messages/ai-messages.css";

/**
 * Partial draft from a `propose_report` call that is still streaming
 * its input (or executing). The AI SDK exposes the progressively-
 * parsed JSON on `part.input`, so title → subtitle → columns pop into
 * the panel in the order the model writes them — the panel visibly
 * builds during what used to be the turn's long silent phase.
 *
 * Only the LAST propose part of the LAST assistant message counts,
 * and only while a turn is actually in flight — a part stranded in
 * `input-available` by an aborted turn must not freeze the panel.
 */
function extractPendingDraft(messages: UIMessage[]): PendingDraft | null {
  const last = messages[messages.length - 1];
  if (!last || last.role !== "assistant") return null;
  for (let i = last.parts.length - 1; i >= 0; i--) {
    const part = last.parts[i] as { type?: string; state?: string; input?: unknown };
    if (part?.type !== "tool-propose_report") continue;
    // Newest propose part settled → the data part has taken over.
    if (part.state !== "input-streaming" && part.state !== "input-available") return null;
    const input = (part.input ?? {}) as Record<string, unknown>;
    return {
      ...(typeof input.title === "string" ? { title: input.title } : {}),
      ...(typeof input.subtitle === "string" ? { subtitle: input.subtitle } : {}),
      ...(Array.isArray(input.columns)
        ? { columns: input.columns.filter((c): c is string => typeof c === "string") }
        : {}),
    };
  }
  return null;
}

interface ReportAuthoringChatProps {
  /**
   * When present, the chat bootstraps in edit-mode for that saved AI
   * report — the panel pre-fills from `getDefinition` and agent saves
   * route through `update_report`. Absent = fresh chat.
   */
  editReportKey?: string;
}

export function ReportAuthoringChat({ editReportKey }: ReportAuthoringChatProps) {
  return (
    <ReportChatBody
      key={editReportKey ?? "new"}
      {...(editReportKey ? { editReportKey } : {})}
    />
  );
}

function ReportChatBody({ editReportKey }: ReportAuthoringChatProps) {
  const {
    messages,
    send,
    stop,
    status,
    isBusy,
    isWaitingForAssistant,
    error,
    currentDraft,
    currentHtml,
    savedKey,
    isEditHydrating,
    saveMutation,
    updateMutation,
    updateTemplateMutation,
  } = useReportAuthoringChat(editReportKey ? { editReportKey } : {});

  // While the model streams a propose_report call, mirror the partial
  // definition into the panel. Guarded on isBusy so an aborted turn's
  // stranded part can't freeze the panel in the building state.
  const pendingDraft = isBusy ? extractPendingDraft(messages) : null;

  return (
    <div className="flex h-full min-h-0 w-full">
      {/* Left — chat pane (fixed 340 px, white). */}
      <div className="flint-ai-chat-surface flex min-h-0 w-[340px] shrink-0 flex-col bg-paper">
        <div className="flex shrink-0 items-center border-b border-line px-3 py-2">
          <Link
            to="/reports"
            className="-mx-2 inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-muted transition-colors hover:bg-bg hover:text-ink focus-visible:outline-2 focus-visible:outline-sky-600 focus-visible:outline-offset-2"
          >
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden />
            Back to reports
          </Link>
        </div>
        {/* Conversation owns the scroll region: StickToBottom pins to
            the newest streamed token; the button surfaces on scroll-up. */}
        <Conversation className="min-h-0 flex-1">
          <ConversationContent className="!gap-3 px-3 pb-3 pt-3">
            {isEditHydrating ? (
              <div className="flex items-center gap-2 pt-1 text-xs text-muted">
                <Loader2
                  className="h-3 w-3 animate-spin text-sky-600"
                  strokeWidth={2.5}
                  aria-hidden
                />
                <span>Loading report…</span>
              </div>
            ) : messages.length === 0 ? (
              <SuggestedPrompts onPick={send} />
            ) : (
              <>
                {messages.map((message: UIMessage, index) => {
                  if (message.role === "user") {
                    return <HumanMessage key={message.id} message={message} />;
                  }
                  const isLast = index === messages.length - 1;
                  return (
                    <AIMessage key={message.id} message={message} isLoading={isBusy && isLast} />
                  );
                })}
                {isWaitingForAssistant ? <ThinkingMessage /> : null}
                {error ? (
                  <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    Something went wrong — {error.message}. Try sending that again.
                  </div>
                ) : null}
              </>
            )}
          </ConversationContent>
          <ConversationScrollButton className="bg-paper text-ink hover:bg-paper" />
        </Conversation>

        <ChatComposer
          status={status as "submitted" | "streaming" | "ready" | "error"}
          loadingThread={isEditHydrating}
          onSubmit={({ text }) => send(text)}
          onStop={stop}
          showAttachments={false}
          showVoice={false}
          placeholder="Describe a report…"
        />
      </div>

      {/* Right — slate gutter with the white report card inside.
          overflow-hidden: the card scrolls internally, the gutter
          never does. */}
      <div className="flex flex-1 min-w-0 min-h-0 flex-col overflow-hidden border-l border-line bg-slate-100">
        <PreviewPane
          draft={currentDraft}
          pending={pendingDraft}
          html={currentHtml}
          {...(savedKey ? { savedKey } : {})}
          editMode={!!editReportKey}
          saveMutation={saveMutation}
          updateMutation={updateMutation}
          updateTemplateMutation={updateTemplateMutation}
        />
      </div>
    </div>
  );
}
